import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChatUiState } from '../models/chat.models';

/**
 * ChatUiStateService — Global reactive state for the chat widget UI.
 *
 * Manages open/close, loading, error, and unread badge count.
 */
@Injectable({ providedIn: 'root' })
export class ChatUiStateService {

  private readonly initialState: ChatUiState = {
    isOpen: false,
    isLoading: false,
    unreadCount: 0,
    error: null
  };

  private readonly stateSubject = new BehaviorSubject<ChatUiState>({ ...this.initialState });

  /** Observable stream of the current UI state. */
  readonly state$: Observable<ChatUiState> = this.stateSubject.asObservable();

  /** Current snapshot (non-reactive). */
  get snapshot(): ChatUiState {
    return this.stateSubject.value;
  }

  open(): void {
    this.patch({ isOpen: true, unreadCount: 0 });
  }

  close(): void {
    this.patch({ isOpen: false });
  }

  toggle(): void {
    this.snapshot.isOpen ? this.close() : this.open();
  }

  setLoading(isLoading: boolean): void {
    this.patch({ isLoading });
  }

  setError(error: string | null): void {
    // Only stop loading when there is an actual error.
    // Calling setError(null) to clear previous errors should NOT cancel loading.
    if (error !== null) {
      this.patch({ error, isLoading: false });
    } else {
      this.patch({ error: null });
    }
  }

  incrementUnread(): void {
    if (!this.snapshot.isOpen) {
      this.patch({ unreadCount: this.snapshot.unreadCount + 1 });
    }
  }

  resetUnread(): void {
    this.patch({ unreadCount: 0 });
  }

  // ─── Private ──────────────────────────────────────────────────────

  private patch(partial: Partial<ChatUiState>): void {
    this.stateSubject.next({ ...this.snapshot, ...partial });
  }
}
